/**
 * In-memory cache for Xtream category lists.
 *
 * Category lists are small but requested from multiple list views. Cache is
 * scoped by current Xtream host and content type.
 */

import xtreamAPI, { XtreamCategory } from '../api/xtreamAPI';

const CACHE_TTL_MS = 4 * 60 * 60 * 1000;

const cache = new Map<string, { data: XtreamCategory[]; ts: number }>();
const inflight = new Map<string, Promise<XtreamCategory[]>>();

function key(type: 'movies' | 'series'): string {
  return `${xtreamAPI.getHost()}:${type}`;
}

async function getCachedCategories(
  type: 'movies' | 'series',
  fetcher: () => Promise<XtreamCategory[]>,
): Promise<XtreamCategory[]> {
  const cacheKey = key(type);
  const hit = cache.get(cacheKey);
  if (hit && Date.now() - hit.ts < CACHE_TTL_MS) return hit.data;
  const pending = inflight.get(cacheKey);
  if (pending) return pending;
  const promise = fetcher().then((data) => {
    cache.set(cacheKey, { data, ts: Date.now() });
    inflight.delete(cacheKey);
    return data;
  }).catch((error) => {
    inflight.delete(cacheKey);
    throw error;
  });
  inflight.set(cacheKey, promise);
  return promise;
}

export function getCachedMovieCategories(): Promise<XtreamCategory[]> {
  return getCachedCategories('movies', () => xtreamAPI.getMovieCategories());
}

export function getCachedSeriesCategories(): Promise<XtreamCategory[]> {
  return getCachedCategories('series', () => xtreamAPI.getSeriesCategories());
}

export function invalidateXtreamCategoriesCache(): void {
  cache.clear();
  inflight.clear();
}
